import "@/index.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { captureRefCode } from "@/lib/refCode";
import { captureContactId } from "@/lib/contactId";

// Eager-loaded — landing page must paint immediately on first hit
import LandingPage from "@/pages/LandingPage";

// All other routes are code-split out of the initial bundle
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const CoursesPage = lazy(() => import("@/pages/CoursesPage"));
const CourseDetailPage = lazy(() => import("@/pages/CourseDetailPage"));
const PackagePage = lazy(() => import("@/pages/ProgramsPage"));
const UpcomingPage = lazy(() => import("@/pages/UpcomingPage"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const ExplorerPassPage = lazy(() => import("@/pages/ExplorerPassPage"));
const ExplorerPassCheckoutPage = lazy(() => import("@/pages/ExplorerPassCheckoutPage"));
const MembershipPage = lazy(() => import("@/pages/MembershipPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const UserDashboard = lazy(() => import("@/pages/UserDashboard"));
const CoursePlayerPage = lazy(() => import("@/pages/CoursePlayerPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const AgentOnboardingPage = lazy(() => import("@/pages/AgentOnboardingPage"));
const SetPasswordPage = lazy(() => import("@/pages/SetPasswordPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const CertificateVerifyPage = lazy(() => import("@/pages/CertificateVerifyPage"));
const ResellerPage = lazy(() => import("@/pages/ResellerPage"));
const FreeAffiliatePage = lazy(() => import("@/pages/FreeAffiliatePage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const RefundPage = lazy(() => import("@/pages/RefundPage"));

function RefCodeCapture() {
  useEffect(() => { captureRefCode(); captureContactId(); }, []);
  return null;
}

/* Scroll behaviour for every navigation, in one place.

   React Router leaves the window where it was on a route change, so a click made
   halfway down one page lands halfway down the next — hence the reset. A
   back/forward step is left alone: the browser restores the previous offset, and
   jumping to the top would throw the reader's place away.

   A hash target ("/#skill-paths") scrolls to its section instead. It cannot be
   left to the destination page, because a hash link followed from the page that
   already owns the anchor unmounts nothing — the route stays "/" and only the
   hash changes, so a page-level mount effect never fires. The element is polled
   for rather than read once, since it may belong to a lazily loaded route or to
   a section still waiting on its data. */
const HASH_TARGET_TIMEOUT = 2000;

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (!hash) {
      if (navigationType !== "POP") window.scrollTo(0, 0);
      return undefined;
    }

    const id = decodeURIComponent(hash.slice(1));
    const deadline = Date.now() + HASH_TARGET_TIMEOUT;
    let frame = 0;

    const seek = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (Date.now() < deadline) frame = requestAnimationFrame(seek);
    };

    frame = requestAnimationFrame(seek);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, navigationType]);

  return null;
}

function RouteFallback() {
  return (
    <div className="genryz-public-light min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RefCodeCapture />
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:courseId" element={<CourseDetailPage />} />
            <Route path="/package" element={<PackagePage />} />
            <Route path="/upcoming" element={<UpcomingPage />} />
            <Route path="/programs" element={<Navigate to="/package" replace />} />
            <Route path="/learning" element={<LearningPage />} />
            <Route path="/explorer-pass" element={<ExplorerPassPage />} />
            <Route path="/explorer-pass/checkout" element={<ExplorerPassCheckoutPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/bundle" element={<Navigate to="/package" replace />} />
            <Route path="/bundles" element={<Navigate to="/package" replace />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/dashboard/course/:courseId" element={<CoursePlayerPage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/partner" element={<ResellerPage />} />
            <Route path="/freeaffiliate" element={<FreeAffiliatePage />} />
            <Route path="/partner/portal" element={<AgentDashboard />} />
            <Route path="/partner/onboard" element={<AgentOnboardingPage />} />
            <Route path="/agent" element={<AgentDashboard />} />
            <Route path="/agent/onboard" element={<AgentOnboardingPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/verify-certificate/:certId" element={<CertificateVerifyPage />} />
            <Route path="/reseller" element={<ResellerPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund" element={<RefundPage />} />
          </Routes>
        </Suspense>
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
